import { addSignalAttribute, identify, removeSignalAttribute } from '@dash0hq/sdk-web';
import { loadConfig } from './config';

export interface ObservabilityIdentity {
  clerkId?: string;
  email?: string;
  clerkRole?: string;
  appRole?: string;
  providerType?: string;
  tenant?: string;
  isImpersonating?: boolean;
  impersonatingProviderId?: string;
}

// User-scoped attrs cleared on sign-out. Static dims (app.brand, app.version)
// live in init()'s additionalSignalAttributes so anonymous signals keep them.
const USER_ATTR_KEYS = [
  'app.role',
  'app.clerk_role',
  'app.provider_type',
  'app.tenant',
  'app.is_impersonating',
  'app.impersonating_provider_id',
] as const;

let lastKey = '';
let isActive = false;

function isEnabled(): boolean {
  const { enabled, environment } = loadConfig();
  if (!enabled) return false;
  const env = environment.trim().toLowerCase();
  return env !== 'prod' && env !== 'production';
}

function setAttr(key: string, value: string | undefined): void {
  if (!value) {
    removeSignalAttribute(key);
    return;
  }
  addSignalAttribute(key, value);
}

function setBoolAttr(key: string, value: boolean | undefined): void {
  if (value === undefined) {
    removeSignalAttribute(key);
    return;
  }
  addSignalAttribute(key, value);
}

export function identifyUser(identity: ObservabilityIdentity): void {
  if (!isEnabled()) return;

  const key = JSON.stringify(identity);
  if (key === lastKey) return;
  lastKey = key;
  isActive = true;

  if (identity.clerkId) {
    identify(identity.clerkId, {
      email: identity.email,
      roles: identity.appRole ? [identity.appRole] : undefined,
    });
  }

  setAttr('app.role', identity.appRole);
  setAttr('app.clerk_role', identity.clerkRole);
  setAttr('app.provider_type', identity.providerType);
  setAttr('app.tenant', identity.tenant);
  setBoolAttr('app.is_impersonating', identity.isImpersonating);
  setAttr('app.impersonating_provider_id', identity.impersonatingProviderId);
}

export function resetObservabilityIdentity(): void {
  if (!isActive) return;
  lastKey = '';
  isActive = false;
  for (const attr of USER_ATTR_KEYS) removeSignalAttribute(attr);
}

/** For tests. */
export function _resetIdentityModuleState(): void {
  lastKey = '';
  isActive = false;
}
